import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import Modal from '../../../../../ui/canon/Modal';
import Button from '../../../../../ui/canon/Button';
import Input from '../../../../../ui/canon/Input';
import { 
  InfoIcon, 
  CheckCircleIcon, 
  CalendarIcon, 
  ClockIcon, 
  MailIcon 
} from 'lucide-react';

// Generate 30-minute intervals from 9 AM to 8 PM
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour <= 20; hour += 1) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
      slots.push({ value: time, label: displayTime });
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

const PauseSchedulingDialog = ({ 
  open, 
  onOpenChange, 
  onSchedule, 
  currentProgress = 0, 
  questionsAnswered = 0 
}) => {
  const intl = useIntl();
  const [resumeDate, setResumeDate] = useState('');
  const [resumeTime, setResumeTime] = useState('');
  const [scheduled, setScheduled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [reminderStatus, setReminderStatus] = useState(null); // 'scheduled' | 'failed' | 'disabled' | null

  const handleSchedule = async () => {
    if (!resumeDate || !resumeTime) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const result = await onSchedule({ date: resumeDate, time: resumeTime });

      if (result?.success) {
        const status = result?.progress?.resumeReminderStatus || null;
        setReminderStatus(status);
        setScheduled(true);

        // Auto-close after 3 seconds
        setTimeout(() => {
          onOpenChange(false);
          setScheduled(false);
          setResumeDate('');
          setResumeTime('');
          setReminderStatus(null);
        }, 3000);
      } else {
        const errors = result?.errors?.filter(Boolean) || [];
        setErrorMessage(errors.length ? errors.join(', ') : 'Unable to pause your plan right now. Please try again.');
      }
    } catch (e) {
      setErrorMessage(e?.message || 'Unable to pause your plan right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setScheduled(false);
    setIsSubmitting(false);
    setErrorMessage('');
    setResumeDate('');
    setResumeTime('');
    setReminderStatus(null);
  };

  const formatDisplayTime = (time) => {
    const slot = timeSlots.find(s => s.value === time);
    return slot ? slot.label : time;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    // Avoid YYYY-MM-DD being parsed as UTC by some browsers (can display previous day in local tz)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0, 0);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      overlayClassName="fixed inset-0 z-50 bg-black/60 animate-modal-fade-in"
    >
      <div className="p-6">
        {!scheduled ? (
          <>
            <h2 className="text-xl font-semibold text-succession-text-primary mb-2">
              {intl.formatMessage({ id: 'succession.plans.pause_title' })}
            </h2>
            <p className="text-succession-text-secondary mb-6">
              {intl.formatMessage({ id: 'succession.plans.pause_description' })}
            </p>
            
            {/* Progress Summary */}
            <div className="bg-succession-bg-secondary rounded-lg p-4 space-y-2 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.pause_current_progress' })}</span>
                <span className="text-sm font-semibold text-succession-text-primary">
                  {currentProgress}%
                </span>
              </div>
              <div className="w-full bg-succession-bg-primary rounded-full h-2">
                <div
                  className="bg-succession-primary h-2 rounded-full transition-all"
                  style={{ width: `${currentProgress}%` }}
                />
              </div>
              <p className="text-xs text-succession-text-muted">
                {intl.formatMessage({ id: 'succession.plans.pause_questions_saved' }, { count: questionsAnswered })}
              </p>
            </div>
            
            {/* Date and Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Date Picker */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-succession-text-primary">
                  <CalendarIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.plans.pause_select_date' })}
                </label>
                <Input 
                  type="date" 
                  value={resumeDate} 
                  onChange={(e) => setResumeDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
              
              {/* Time Picker */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-succession-text-primary">
                  <ClockIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.plans.pause_select_time' })}
                </label>
                <select
                  value={resumeTime}
                  onChange={(e) => setResumeTime(e.target.value)}
                  className="w-full rounded-md border border-succession-border bg-succession-bg-primary px-3 py-2 text-sm text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
                >
                  <option value="">{intl.formatMessage({ id: 'succession.plans.pause_choose_time' })}</option>
                  {timeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Selected Date/Time Preview */}
            {resumeDate && resumeTime && (
              <div className="bg-succession-primary/10 border border-succession-primary/20 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.plans.pause_scheduled_for' })}</p>
                <p className="text-sm text-succession-text-secondary">
                  {formatDisplayDate(resumeDate)}
                </p>
                <p className="text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.plans.pause_at_time' }, { time: formatDisplayTime(resumeTime) })}
                </p>
              </div>
            )}

            {/* Reminder Info */}
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <MailIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {intl.formatMessage({ id: 'succession.plans.pause_reminders_title' })}
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {intl.formatMessage({ id: 'succession.plans.pause_reminders_description' })}
                  </p>
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <InfoIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-red-900 dark:text-red-100">{intl.formatMessage({ id: 'succession.plans.pause_error_title' })}</p>
                    <p className="text-xs text-red-700 dark:text-red-300">{errorMessage}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose}>
                {intl.formatMessage({ id: 'succession.plans.pause_cancel' })}
              </Button>
              <Button
                onClick={handleSchedule}
                disabled={!resumeDate || !resumeTime || isSubmitting}
                variant="primary"
              >
                {isSubmitting ? intl.formatMessage({ id: 'succession.plans.pause_scheduling' }) : intl.formatMessage({ id: 'succession.plans.pause_schedule_and_pause' })}
              </Button>
            </div>
          </>
        ) : (
          <div className="py-4">
            <div className="text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-950 flex items-center justify-center mx-auto">
                <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.plans.pause_success_title' })}</h2>
                <p className="text-sm text-succession-text-secondary">
                  {reminderStatus === 'scheduled'
                    ? intl.formatMessage({ id: 'succession.plans.pause_success_with_reminder' })
                    : intl.formatMessage({ id: 'succession.plans.pause_success_no_reminder' })}
                </p>
              </div>
              <div className="bg-succession-bg-secondary rounded-lg p-4 max-w-md mx-auto">
                <p className="text-sm font-medium text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.plans.pause_resume_on' })}</p>
                <p className="text-sm text-succession-text-secondary">
                  {formatDisplayDate(resumeDate)}
                </p>
                <p className="text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.plans.pause_at_time' }, { time: formatDisplayTime(resumeTime) })}
                </p>
              </div>
              {reminderStatus && reminderStatus !== 'scheduled' && (
                <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {intl.formatMessage({ id: 'succession.plans.pause_reminder_unavailable' })}
                  </p>
                </div>
              )}
              <Button 
                onClick={() => window.location.href = '/succession/plans'}
                variant="primary"
                className="mt-4"
              >
                {intl.formatMessage({ id: 'succession.plans.pause_return_to_dashboard' })}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PauseSchedulingDialog;
